// Curated tenant website templates.
// Each template = a different ordering / treatment of the same shared
// section components. Sections read `templateStyle` from TenantThemeContext
// to switch internal layout primitives (e.g. Hero variant).

export const TEMPLATES = [
    {
        id: 'forest',
        name: 'Forest',
        nameBn: 'ফরেস্ট',
        tagline: 'Premium, brochure-grade craftsmanship',
        taglineBn: 'প্রিমিয়াম, ব্রোশিওর মানের কারিগরি',
        recommendedFor: 'High-end and specialist garages',
        heroVariant: 'forest',
        motionIntensity: 'high',
        uses3D: true,
        // Visual tokens consumed by section primitives.
        tokens: {
            sectionPadding: 'py-16 md:py-20',
            headerVariant: 'centered',     // bar + display heading + tagline
            headerHeading: 'font-display text-4xl md:text-6xl tracking-wide',
            cardVariant: 'glass',          // soft frosted card
            cardHover: 'hover:-translate-y-1 hover:shadow-2xl',
            divider: 'bracket',            // section dividers between blocks
            eyebrowStyle: 'tracking-[0.32em]',
            ctaStyle: 'magnetic',
            featureGrid: 'grid sm:grid-cols-2 lg:grid-cols-3 gap-6'
        }
    },
    {
        id: 'workshop',
        name: 'Workshop',
        nameBn: 'ওয়ার্কশপ',
        tagline: 'Bold industrial energy',
        taglineBn: 'সাহসী ও শিল্পসুলভ',
        recommendedFor: 'Volume garages and busy mechanic shops',
        heroVariant: 'workshop',
        motionIntensity: 'high',
        uses3D: true,
        tokens: {
            sectionPadding: 'py-12 md:py-16',
            headerVariant: 'stamped',      // big numerals overlay + caps
            headerHeading: 'font-display text-5xl md:text-7xl tracking-[0.04em] uppercase',
            cardVariant: 'numbered',       // big numbers on each card, hard edges
            cardHover: 'hover:border-[var(--tenant-primary)]',
            divider: 'stripe',
            eyebrowStyle: 'tracking-[0.4em] text-[var(--tenant-primary)]',
            ctaStyle: 'block',
            featureGrid: 'grid sm:grid-cols-2 lg:grid-cols-3 gap-4'
        }
    },
    {
        id: 'showroom',
        name: 'Showroom',
        nameBn: 'শোরুম',
        tagline: 'Clean automotive luxury',
        taglineBn: 'মার্জিত ও বিলাসবহুল',
        recommendedFor: 'Detailers and premium service centers',
        heroVariant: 'showroom',
        motionIntensity: 'medium',
        uses3D: false,
        tokens: {
            sectionPadding: 'py-20 md:py-24',
            headerVariant: 'minimal',      // tiny eyebrow + clean ink heading
            headerHeading: 'font-headline font-semibold text-4xl md:text-6xl tracking-tight',
            cardVariant: 'outline',        // hairline outline + lots of whitespace
            cardHover: 'hover:bg-[var(--tenant-surface-alt)]',
            divider: 'hairline',
            eyebrowStyle: 'tracking-[0.5em] text-[var(--tenant-muted)]',
            ctaStyle: 'pill',
            featureGrid: 'grid md:grid-cols-2 gap-8'
        }
    },
    {
        id: 'express',
        name: 'Express',
        nameBn: 'এক্সপ্রেস',
        tagline: 'Fast service, energetic vibe',
        taglineBn: 'দ্রুত সার্ভিস, প্রাণবন্ত',
        recommendedFor: 'Quick-service shops, oil changes, tire centers',
        heroVariant: 'express',
        motionIntensity: 'high',
        uses3D: true,
        tokens: {
            sectionPadding: 'py-16 md:py-24',
            headerVariant: 'underlined',   // tight heading with primary underline + chevron
            headerHeading: 'font-headline font-extrabold text-4xl md:text-5xl tracking-tight',
            cardVariant: 'solid',          // full primary-soft fill, energetic
            cardHover: 'hover:-translate-y-0.5 hover:shadow-lg',
            divider: 'ticker',
            eyebrowStyle: 'tracking-[0.28em] text-[var(--tenant-primary)]',
            ctaStyle: 'block',
            featureGrid: 'grid grid-cols-2 lg:grid-cols-4 gap-3'
        }
    },
    {
        id: 'heritage',
        name: 'Heritage',
        nameBn: 'হেরিটেজ',
        tagline: 'Trust-first, traditional',
        taglineBn: 'বিশ্বস্ত ও ঐতিহ্যবাহী',
        recommendedFor: 'Family-run, long-established garages',
        heroVariant: 'heritage',
        motionIntensity: 'low',
        uses3D: false,
        tokens: {
            sectionPadding: 'py-16 md:py-20',
            headerVariant: 'serif',        // serif-styled heading w/ ornament
            headerHeading: 'font-headline italic font-semibold text-4xl md:text-6xl tracking-tight',
            cardVariant: 'classic',        // warm beige card with thicker border
            cardHover: 'hover:shadow-md',
            divider: 'ornament',
            eyebrowStyle: 'tracking-[0.45em]',
            ctaStyle: 'pill',
            featureGrid: 'grid sm:grid-cols-2 gap-6'
        }
    }
];

export const DEFAULT_TEMPLATE_ID = 'forest';

export function getTemplate(id) {
    return TEMPLATES.find(t => t.id === id) || TEMPLATES[0];
}

export function getTemplateTokens(id) {
    return getTemplate(id).tokens || {};
}

// Section keys — order respected on landing page; each section's enabled
// state is read from `enabledSections[sectionKey]` (default true unless
// overridden below).
export const SECTION_KEYS = [
    'hero', // locked on
    'trust_strip',
    'why_choose_us',
    'specializations',
    'services',         // locked on
    'process',
    'job_tracker',
    'before_after',
    'convenience',
    'real_work_reel',
    'reviews',
    'team',
    'booking',
    'blog_teaser',
    'map',              // locked on
    'faq',
    'final_cta'         // locked on
];

export const LOCKED_SECTIONS = new Set(['hero', 'services', 'map', 'final_cta']);

// Per-template render order on the landing page. Same component pool, different
// narrative. Consumed by `LandingPage.LandingComposer` AND by `TenantLayout` so
// the top-nav items appear in the same order as the user will encounter them.
export const SECTION_ORDERS = {
    forest: ['hero', 'trust', 'why', 'specializations', 'services', 'parts', 'process', 'jobtracker', 'beforeAfter', 'convenience', 'realWork', 'reviews', 'team', 'booking', 'blog', 'map', 'faq', 'finalcta'],
    workshop: ['hero', 'trust', 'why', 'process', 'specializations', 'services', 'parts', 'jobtracker', 'beforeAfter', 'realWork', 'reviews', 'team', 'convenience', 'booking', 'blog', 'map', 'faq', 'finalcta'],
    showroom: ['hero', 'trust', 'services', 'beforeAfter', 'realWork', 'specializations', 'why', 'parts', 'team', 'reviews', 'process', 'jobtracker', 'convenience', 'booking', 'blog', 'map', 'faq', 'finalcta'],
    express: ['hero', 'trust', 'booking', 'services', 'why', 'process', 'parts', 'jobtracker', 'beforeAfter', 'realWork', 'reviews', 'convenience', 'team', 'specializations', 'blog', 'map', 'faq', 'finalcta'],
    heritage: ['hero', 'trust', 'why', 'reviews', 'team', 'services', 'specializations', 'beforeAfter', 'realWork', 'parts', 'process', 'jobtracker', 'convenience', 'booking', 'blog', 'map', 'faq', 'finalcta']
};

export function getSectionOrder(templateId) {
    return SECTION_ORDERS[templateId] || SECTION_ORDERS.forest;
}

export const SECTION_DEFAULTS = {
    hero: true,
    trust_strip: true,
    why_choose_us: true,
    specializations: false, // auto-on if any added
    services: true,
    process: true,
    job_tracker: true,
    before_after: false,    // auto-on if >=3 pairs
    convenience: false,     // auto-on if any sub-item enabled
    real_work_reel: false,  // auto-on if any video added
    reviews: true,
    team: false,
    booking: true,
    blog_teaser: false,     // auto-on if >=1 post
    map: true,
    faq: true,
    final_cta: true
};

export const SECTION_LABELS = {
    hero: { en: 'Hero', bn: 'হিরো' },
    trust_strip: { en: 'Trust strip', bn: 'বিশ্বাসের ব্যাজ' },
    why_choose_us: { en: 'Why choose us (AI)', bn: 'কেন আমরা' },
    specializations: { en: 'Specializations & certifications', bn: 'বিশেষজ্ঞতা' },
    services: { en: 'Services showcase', bn: 'সার্ভিস তালিকা' },
    process: { en: 'How it works', bn: 'প্রক্রিয়া' },
    job_tracker: { en: 'Live job tracker', bn: 'লাইভ ট্র্যাকিং' },
    before_after: { en: 'Before / after gallery', bn: 'আগে / পরে' },
    convenience: { en: 'Convenience promise', bn: 'সুবিধার প্রতিশ্রুতি' },
    real_work_reel: { en: 'Real work videos', bn: 'কাজের ভিডিও' },
    reviews: { en: 'Reviews & testimonials', bn: 'রিভিউ' },
    team: { en: 'Meet the team', bn: 'টিম' },
    booking: { en: 'Booking form', bn: 'বুকিং' },
    blog_teaser: { en: 'Blog teaser', bn: 'ব্লগ' },
    map: { en: 'Map & visit us', bn: 'ম্যাপ' },
    faq: { en: 'FAQ', bn: 'প্রশ্ন-উত্তর' },
    final_cta: { en: 'Final call-to-action', bn: 'শেষ আহ্বান' }
};
